import React, { useState, useEffect, useContext, useRef } from "react";

import { useImmerReducer } from "use-immer";
import createAuth0Client from "@auth0/auth0-spa-js";

const DEFAULT_REDIRECT_CALLBACK = () => {
  window.history.replaceState({}, document.title, window.location.pathname);
};

// Errores de Auth0 que indican que la sesión venció y solo se recupera iniciando sesión otra vez.
const SESSION_EXPIRED_ERRORS = [
  "login_required",
  "consent_required",
  "interaction_required",
  "invalid_grant",
  "missing_refresh_token",
];

const isSessionExpiredError = (error) =>
  SESSION_EXPIRED_ERRORS.includes(error?.error);

const initialState = {
  user: null,
  profile: null,
  loading: true,
  popupOpen: false,
  auth0Client: null,
  isAuthenticated: false
};

function actionReducer(draft, action) {
  switch (action.type) {
    case "CHANGE_FIELD":
      draft[action.field] = action.payload;
      return;
    case "CHANGE_FIELDS":
      const keys = Object.keys(action.values);
      for (let key of keys) draft[key] = action.values[key];
      return;
    default:
      return;
  }
}

export const Auth0Context = React.createContext();
export const useAuth0 = () => useContext(Auth0Context);

export default function Auth0Provider({
  children,
  onRedirectCallback = DEFAULT_REDIRECT_CALLBACK,
  ...initOptions
}) {
  const [loading, setLoading] = useState(true);
  const [state, dispatch] = useImmerReducer(actionReducer, initialState);
  const auth0ClientRef = useRef(null);
  const isAuthenticatedRef = useRef(false);
  const redirectingRef = useRef(false);

  auth0ClientRef.current = state.auth0Client;
  isAuthenticatedRef.current = state.isAuthenticated;

  // Envía al login de Auth0 una sola vez y regresa a la pantalla actual al volver.
  const redirectToLogin = () => {
    if (redirectingRef.current || !auth0ClientRef.current) return;
    redirectingRef.current = true;
    auth0ClientRef.current.loginWithRedirect({
      appState: {
        targetUrl: `${window.location.pathname}${window.location.search}`,
      },
    });
  };

  const getTokenSilently = async (...p) => {
    try {
      return await auth0ClientRef.current.getTokenSilently(...p);
    } catch (error) {
      // Solo aplica a sesiones iniciadas; las páginas públicas no deben ir al login.
      if (isAuthenticatedRef.current && isSessionExpiredError(error)) {
        redirectToLogin();
      }
      throw error;
    }
  };

  useEffect(() => {
    const initAuth0 = async () => {
      const auth0FromHook = await createAuth0Client(initOptions);
      dispatch({
        type: "CHANGE_FIELD",
        field: "auth0Client",
        payload: auth0FromHook
      });

      // Al rechazar el consentimiento Auth0 regresa con ?error=; se cierra su sesión
      // para mostrar el formulario de login en lugar de repetir la autorización.
      const params = new URLSearchParams(window.location.search);
      if (params.has("error") && params.has("state")) {
        auth0FromHook.logout({ returnTo: window.location.origin });
        return;
      }

      if (
        window.location.search.includes("code=") &&
        window.location.search.includes("state=")
      ) {
        const { appState } = await auth0FromHook.handleRedirectCallback();
        onRedirectCallback(appState);
      }

      const isAuthenticated = await auth0FromHook.isAuthenticated();
      dispatch({
        type: "CHANGE_FIELD",
        field: "isAuthenticated",
        payload: isAuthenticated
      });

      if (isAuthenticated) {
        const user = await auth0FromHook.getUser();
        dispatch({ type: "CHANGE_FIELD", field: "user", payload: user });
      }

      setLoading(false);
    };

    initAuth0();
    // eslint-disable-next-line
  }, []);

  const handleRedirectCallback = async () => {
    setLoading(true);
    await state.auth0Client.handleRedirectCallback();
    const user = await state.auth0Client.getUser();

    setLoading(false);
    dispatch({
      type: "CHANGE_FIELDS",
      values: {
        user: user,
        isAuthenticated: true
      }
    });
  };

  return (
    <Auth0Context.Provider
      value={{
        loading,
        dispatch,
        user: state.user,
        profile: state.profile,
        handleRedirectCallback,
        isAuthenticated: state.isAuthenticated,
        logout: (...p) => state.auth0Client.logout(...p),
        getIdTokenClaims: (...p) => state.auth0Client.getIdTokenClaims(...p),
        loginWithRedirect: (...p) => state.auth0Client.loginWithRedirect(...p),
        redirectToLogin,
        getTokenSilently,
        getTokenWithPopup: (...p) => state.auth0Client.getTokenWithPopup(...p)
      }}
    >
      {children}
    </Auth0Context.Provider>
  );
}
